import { BUTTON_SIZE, BUTTON_TYPE, DSButton } from '@repo/design-system/index'
import { QIcon_chevron_left } from '@repo/icons/index'
import { ActivityComponentType } from '@stackflow/react'
import { useEffect, useState } from 'react'
import { useEventTracking } from '../../../helpers/gtmHelper'
import { ILesson } from '../../../model/lesson.interface'
import apis from '../../../modules/apis'
import { axiosGet } from '../../../modules/axiosHelper'
import { useCurrentUser } from '../../../modules/context/UserContext'
import useAppRouter from '../../../modules/hooks/common/useAppRouter'
import useWebviewNavigation from '../../../modules/hooks/webview/common/useWebviewNavigation'
import { n_lesson_prep } from '../../../modules/i18n/strings/auto/wording'
import useTranslation from '../../../modules/i18n/useTranslation'
import urls from '../../../modules/urls'
import { preventClose, saveMoveSubTab } from '../../../pages/[lang]/portal/lessons/preparation/[lessonId]'
import { useLessonsStack } from '../../../pages/[lang]/webview/lessons'
import { TAB } from '../../materials/const'
import PreparationAnswerNotSaved from '../../new-portal/lessons/preparation/PreparationAnswerNotSaved'
import PreparationNotYet from '../../new-portal/lessons/preparation/PreparationNotYet'
import UnifiedTextbody from '../../new-portal/lessons/preparation/UnifiedTextBody'
import { handleSubmitAnswer } from '../../new-portal/materials/QuestionSelect/QuestionSelectPanel'
import WebviewStackContainer from '../common/WebviewStackContainer'

export const PreparationBar = ({ pop, lessonStartDate, lessonStartTime }) => {
  const { t } = useTranslation()

  return (
    <div className="flex w-screen flex-row items-center">
      <div className="ml-[20px] mr-[62.5px] " onClick={pop}>
        <QIcon_chevron_left className="h-24 w-24 text-black" />
      </div>
      <div className="flex flex-row items-center space-x-8">
        <span className="text-[16px] font-medium text-black">{lessonStartDate}</span>
        <div className="mx-[8px] h-[16px] w-[1px] bg-gray-200"></div>
        <span className="text-[16px] font-medium text-black">{lessonStartTime}</span>
      </div>
    </div>
  )
}

interface ILessonPreparationActivity {
  params: {
    lessonId: string
    type: string
    isReview: boolean
  }
}

export const Title = ({ lessonStartDate, lessonStartTime }) => {
  return (
    <div className="flex flex-row items-center space-x-8">
      <span className="text-[16px] font-medium text-black">{lessonStartDate}</span>
      <div className="mx-[8px] h-[16px] w-[1px] bg-gray-200"></div>
      <span className="text-[16px] font-medium text-black">{lessonStartTime}</span>
    </div>
  )
}

const LessonPreparationActivity: ActivityComponentType = ({ params }: ILessonPreparationActivity) => {
  const { useLessonsFlow } = useLessonsStack()
  const { pop, push } = useLessonsFlow()
  const router = useAppRouter({ stackPush: push })
  const { sendPageViewEvent } = useEventTracking()
  const id = params?.lessonId ? params.lessonId : router.query.lessonId
  const pathParams = { lessonId: Number(id) }

  const { currentUser } = useCurrentUser()
  const { t } = useTranslation()
  const [unifiendCurrentTab, setUnifiedCurrentTab] = useState(params.type ? Number(params.type) : TAB.INSIGHT)
  const [tabData, setTabData] = useState<ILesson | undefined>()
  const [isEditable, setIsEditable] = useState([])
  const [answer, setAnswer] = useState('')
  const [selectedShareState, setSelectedShareState] = useState(0)
  const [activeQuestionId, setActiveQuestionId] = useState(null)
  const [data, setData] = useState(null)
  const [lesson, setLesson] = useState<ILesson | undefined>()
  const [lessonStartDate, setLessonDate] = useState('')
  const [lessonStartTime, setLessonTime] = useState('')
  const [isWritingOrFreetalking, setIsWritingOrFreetalking] = useState(false)
  const { popBackToRingleApp } = useWebviewNavigation()

  const [showPrepPopup, setShowPrepPopup] = useState(false)
  const [showSavePopup, setShowSavePopup] = useState(false)
  const [showSaveBeforeOverPopup, setShowSaveBeforeOverPopup] = useState(false)
  const [showCount, setShowCount] = useState(0)
  let count = 0
  let notSaved = false

  const handlePrepPopupClose = () => {
    setShowPrepPopup(false)
  }

  const handleSaveButton = async () => {
    try {
      const apiCalls = []
      const disabled = isWritingOrFreetalking || tabData?.prestudy?.rate === 100 ? false : true

      for (const item of isEditable) {
        if (item.editable && item.student_answer && item.student_answer?.content != '') {
          const apiCall = handleSubmitAnswer({
            id: item.id,
            content: item.student_answer?.content,
            shareStatus: item?.student_answer?.share_status || 0,
            isEditable,
            setIsEditable,
            activeQuestionId: item.question_number,
            lessonId: params.lessonId,
          })
          apiCalls.push(apiCall)
        }
      }

      // 모든 API 호출을 기다림
      await Promise.all(apiCalls)

      window.removeEventListener('beforeunload', (e) => preventClose(e, t(n_lesson_prep.popup_leave_default)))

      if (disabled) {
        // 다 저장은 했지만 수업 준비가 완료되지 않은 케이스 처리
        setShowSavePopup(false)
        setShowPrepPopup(true)
      } else {
        setShowSavePopup(false)
        popBackToRingleApp()
      }
    } catch (error) {
      // 오류 처리
      console.error(error)
    }
  }

  const handleSaveBeforeOverButton = async () => {
    try {
      const apiCalls = []

      for (const item of isEditable) {
        if (item.editable && item.student_answer && item.student_answer?.content != '') {
          const apiCall = handleSubmitAnswer({
            id: item.id,
            content: item.student_answer?.content,
            shareStatus: item?.student_answer?.share_status || 0,
            isEditable,
            setIsEditable,
            activeQuestionId: item.question_number,
            lessonId: params.lessonId,
          })
          apiCalls.push(apiCall)
        }
      }

      // 모든 API 호출을 기다림
      await Promise.all(apiCalls)

      window.removeEventListener('beforeunload', (e) => preventClose(e, t(n_lesson_prep.popup_leave_default)))

      setShowSaveBeforeOverPopup(false)
      router.push(urls.webview.lessons.preparation_over(params.lessonId), {
        stackActivity: 'LessonPreparationOverActivity',
        stackSendData: {
          lessonId: lesson?.id,
        },
      })
    } catch (error) {
      // 오류 처리
      console.error(error)
    }
  }

  const handlePrepExit = async () => {
    if (params?.isReview) {
      popBackToRingleApp()
    } else {
      for (const item of isEditable) {
        const originalAnswer = lesson?.coursekeyquestions_reordering?.find(
          (originalItem) => originalItem.question_number == item.question_number
        )
        console.log(originalAnswer.student_answer?.content)

        if (
          item.editable &&
          (item.student_answer?.content ||
            (originalAnswer.student_answer?.content && item.student_answer?.content.length === 0))
        ) {
          count++
        }
      }

      notSaved = count > 0 ? true : false

      setShowCount(count)

      const disabled = tabData?.prestudy?.rate === 100 ? false : true && !isWritingOrFreetalking

      if (notSaved && count > 0) {
        setShowSavePopup(true)
      }
      if (disabled) {
        setShowPrepPopup(true)
      } else {
        popBackToRingleApp()
      }
    }
  }

  const onClickOverButton = () => {
    for (const item of isEditable) {
      const originalAnswer = lesson?.coursekeyquestions_reordering?.find(
        (originalItem) => originalItem.question_number == item.question_number
      )
      console.log(originalAnswer.student_answer?.content)

      if (
        item.editable &&
        (item.student_answer?.content ||
          (originalAnswer.student_answer?.content && item.student_answer?.content.length === 0))
      ) {
        count++
      }
    }

    notSaved = count > 0 ? true : false

    setShowCount(count)
    if (notSaved) {
      setShowSaveBeforeOverPopup(true)
    } else {
      router.push(urls.webview.lessons.preparation_over(params.lessonId), {
        stackActivity: 'LessonPreparationOverActivity',
        stackSendData: {
          lessonId: lesson?.id,
        },
      })
    }
  }

  useEffect(() => {
    const fetchData = async () => {
      const response = await axiosGet(apis.lessons.preparation.getV5(pathParams))

      if (response) {
        setLesson(response.lesson)
        setTabData(response.lesson)
        setLessonDate(response.lesson.formatted_start_date)
        setLessonTime(response.lesson.formatted_start_time_only)
        setIsWritingOrFreetalking(response.lesson.course.is_writing || response.lesson.course.free_talking)
      }
    }

    fetchData()
  }, [])

  return (
    <WebviewStackContainer
      border={false}
      title={
        <PreparationBar pop={handlePrepExit} lessonStartDate={lessonStartDate} lessonStartTime={lessonStartTime} />
      }
    >
      <PreparationNotYet
        showPrepPopup={showPrepPopup}
        setShowPrepPopup={setShowPrepPopup}
        handlePrepPopupClose={handlePrepPopupClose}
      />
      <PreparationAnswerNotSaved
        showSavePopup={showSavePopup}
        setShowSavePopup={setShowSavePopup}
        count={showCount}
        handleSaveButton={handleSaveButton}
      />
      <PreparationAnswerNotSaved
        showSavePopup={showSaveBeforeOverPopup}
        setShowSavePopup={setShowSaveBeforeOverPopup}
        count={showCount}
        handleSaveButton={handleSaveBeforeOverButton}
      />
      {lesson && (
        <div className="relative h-full">
          <UnifiedTextbody
            lesson={lesson}
            currentUser={currentUser}
            isReview={params?.isReview ? params?.isReview : false}
            currentTab={unifiendCurrentTab}
            setCurrentTab={setUnifiedCurrentTab}
            ringleDocToken={lesson?.ringledoc}
            isEditable={isEditable}
            setIsEditable={setIsEditable}
            setTabData={setTabData}
            answer={answer}
            setAnswer={setAnswer}
            lessonId={id}
            selectedShareState={selectedShareState}
            setSelectedShareState={setSelectedShareState}
            activeQuestionId={activeQuestionId}
            setActiveQuestionId={setActiveQuestionId}
            saveMoveSubTab={saveMoveSubTab}
          />
          {unifiendCurrentTab == TAB.QUESTION_SELECT && !isWritingOrFreetalking && !params?.isReview && (
            <div className="sticky bottom-[0px] w-full bg-white px-[16px] pt-[12px] pb-[40px]">
              <DSButton
                onClick={onClickOverButton}
                size={BUTTON_SIZE.LG}
                type={BUTTON_TYPE.SOLID}
                disabled={tabData?.prestudy?.rate === 100 ? false : true}
              >
                <DSButton.Label>{t(n_lesson_prep.complete.title_done)}</DSButton.Label>
              </DSButton>
            </div>
          )}
        </div>
      )}
    </WebviewStackContainer>
  )
}

export default LessonPreparationActivity
